import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, BedDouble, User, Calendar, FileText, CreditCard, Hotel, ExternalLink } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import HotelAccountingPanel from "@/components/HotelAccountingPanel";
import HotelVoucherPanel from "@/components/HotelVoucherPanel";
import { DashboardLayout } from "@/components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  enquiry:   "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  modified:  "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const TABS = [
  { id: "details", label: "Details", icon: BedDouble },
  { id: "accounting", label: "Accounting", icon: CreditCard },
  { id: "invoice", label: "Invoice", icon: FileText },
  { id: "voucher", label: "Voucher", icon: Hotel },
];

const MEAL_PLAN_OPTIONS = [
  { value: "room_only", label: "Room Only" },
  { value: "breakfast", label: "Breakfast Included" },
  { value: "half_board", label: "Half Board" },
  { value: "full_board", label: "Full Board" },
  { value: "all_inclusive", label: "All Inclusive" },
  { value: "other", label: "Other" },
];

const CANCEL_POLICY_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "free", label: "Free cancellation" },
  { value: "free_until_date", label: "Free until date" },
  { value: "non_refundable", label: "Non-refundable" },
  { value: "partially_refundable", label: "Partially refundable" },
  { value: "custom", label: "Custom" },
];

function Field({ label, value, editable, onSave, type = "text", options }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  if (!editable) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm text-gray-900">{value || "—"}</p>
      </div>
    );
  }
  if (editing) {
    const save = () => { onSave(val); setEditing(false); };
    if (options) {
      return (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
          <select
            className="border border-violet-300 rounded-lg px-3 py-1.5 text-sm w-full"
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={save}
            autoFocus
          >
            {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
          </select>
        </div>
      );
    }
    return (
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
        <input
          type={type}
          className="border border-violet-300 rounded-lg px-3 py-1.5 text-sm w-full"
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={save}
          autoFocus
        />
      </div>
    );
  }
  return (
    <div onClick={() => setEditing(true)} className="cursor-pointer group">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-gray-900 group-hover:text-violet-700 border-b border-transparent group-hover:border-violet-200 inline-block">
        {value || <span className="text-gray-300 italic">Click to edit</span>}
      </p>
    </div>
  );
}

function Toggle({ label, value, onSave }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <button
        onClick={() => onSave(!value)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? "bg-violet-600" : "bg-gray-300"}`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${value ? "translate-x-4" : "translate-x-1"}`} />
      </button>
      <span className="ml-2 text-sm text-gray-700">{value ? "Yes" : "No"}</span>
    </div>
  );
}

function HotelInvoiceSection({ hotelId, hotel, onUpdate }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoices?source_type=hotel_booking&source_id=${hotelId}`, { headers: getAuthHeaders() });
      const found = (r.data || []).find(i => i.invoice_status !== "cancelled") || null;
      setInvoice(found);
    } catch { /**/ } finally { setLoading(false); }
  }, [hotelId]);

  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  const createInvoice = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/hotels/${hotelId}/start-invoice`, {}, { headers: getAuthHeaders() });
      setInvoice(r.data);
      if (onUpdate) onUpdate();
    } catch (e) {
      alert("Failed to create invoice: " + (e.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  };

  if (loading) return <div className="text-gray-400 text-sm py-8 text-center">Loading...</div>;

  if (!invoice) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
        <FileText size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 mb-5">No invoice linked to this hotel booking yet.</p>
        <button
          onClick={createInvoice}
          disabled={creating}
          className="bg-violet-700 hover:bg-violet-800 text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          {creating ? "Creating..." : "Create Invoice"}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider">Linked Invoice</h3>
        <Link
          to={`/invoices/${invoice.id}`}
          className="flex items-center gap-1 text-sm text-violet-700 hover:text-violet-900 font-semibold"
        >
          Open Invoice <ExternalLink size={13} />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Invoice #", value: invoice.invoice_number },
          { label: "Status", value: invoice.invoice_status },
          { label: "Total", value: invoice.invoice_total != null ? `€${parseFloat(invoice.invoice_total).toFixed(2)}` : "—" },
          { label: "Payment", value: invoice.payment_status?.replace(/_/g, " ") || "—" },
        ].map(f => (
          <div key={f.label}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
            <p className="text-sm font-semibold text-gray-900">{f.value || "—"}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400">
        All financial edits (payment status, amounts, VAT type) should be made on the invoice page. The accounting panel also syncs linked fields.
      </p>
    </div>
  );
}

export default function HotelBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hotels/${id}`, { headers: getAuthHeaders() });
      setHotel(r.data);
    } catch { navigate("/hotel-bookings"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/hotels/${id}`, fields, { headers: getAuthHeaders() });
      setHotel(prev => ({ ...prev, ...fields }));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  if (!hotel) return null;

  const nightCount = (() => {
    try { return Math.round((new Date(hotel.check_out) - new Date(hotel.check_in)) / 86400000); }
    catch { return 0; }
  })();

  const cancelPolicyType = hotel.cancellation_policy_type || "";

  return (
    <DashboardLayout>
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
        <button onClick={() => navigate("/hotel-bookings")} className="hover:text-indigo-700 transition-colors">
          Hotel Bookings
        </button>
        <span>/</span>
        <span className="text-gray-700 font-medium truncate max-w-[200px]">{hotel.hotel_name}</span>
      </div>

      {/* Back + title */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/hotel-bookings")} className="text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900">{hotel.hotel_name}</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[hotel.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
              {hotel.status}
            </span>
            {hotel.invoice_number && (
              <span className="text-xs font-mono text-violet-700 bg-violet-50 px-2 py-0.5 rounded-lg">{hotel.invoice_number}</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{hotel.customer_name} &middot; {hotel.check_in} → {hotel.check_out} ({nightCount}n)</p>
        </div>
        {saving && <span className="text-xs text-gray-400">Saving...</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Details tab */}
      {tab === "details" && (
        <div className="space-y-5">
          {/* Status */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Booking Status</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Status" value={hotel.status} editable onSave={v => patch({ status: v })}
                options={["enquiry","confirmed","modified","cancelled"].map(v => ({ value: v, label: v }))} />
              <Field label="Hotel Confirmation #" value={hotel.hotel_confirmation_number} editable
                onSave={v => patch({ hotel_confirmation_number: v })} />
              <Field label="Supplier Reference" value={hotel.supplier_reference} editable
                onSave={v => patch({ supplier_reference: v })} />
            </div>
          </div>

          {/* Guest info */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Guest Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Customer Name" value={hotel.customer_name} editable onSave={v => patch({ customer_name: v })} />
              <Field label="Customer Email" value={hotel.customer_email} editable type="email" onSave={v => patch({ customer_email: v })} />
              <Field label="Customer Phone" value={hotel.customer_phone} editable type="tel" onSave={v => patch({ customer_phone: v })} />
              <Field label="Lead Guest Name" value={hotel.lead_guest_name} editable onSave={v => patch({ lead_guest_name: v })} />
              <Field label="Adults" value={hotel.adults ? String(hotel.adults) : ""} editable type="number" onSave={v => patch({ adults: parseInt(v) || 1 })} />
              <Field label="Children" value={hotel.children ? String(hotel.children) : ""} editable type="number" onSave={v => patch({ children: parseInt(v) || 0 })} />
              <Field label="Child Ages" value={(hotel.child_ages || []).join(", ")} editable
                onSave={v => patch({ child_ages: v.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n)) })} />
              <Field label="Additional Guests" value={(hotel.additional_guests || []).join(", ")} editable
                onSave={v => patch({ additional_guests: v.split(",").map(s => s.trim()).filter(Boolean) })} />
              <Field label="Special Requests" value={hotel.special_requests} editable onSave={v => patch({ special_requests: v })} />
            </div>
          </div>

          {/* Hotel info */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Hotel Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Hotel Name" value={hotel.hotel_name} editable onSave={v => patch({ hotel_name: v })} />
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Star Rating</p>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <button
                      key={s}
                      onClick={() => patch({ star_rating: hotel.star_rating === s ? null : s })}
                      className={`text-xl transition-colors ${s <= (hotel.star_rating || 0) ? "text-yellow-400" : "text-gray-200 hover:text-yellow-300"}`}
                    >
                      ★
                    </button>
                  ))}
                  {hotel.star_rating > 0 && (
                    <button onClick={() => patch({ star_rating: null })} className="text-xs text-gray-400 hover:text-gray-600 ml-1">clear</button>
                  )}
                </div>
              </div>
              <Field label="City" value={hotel.hotel_city} editable onSave={v => patch({ hotel_city: v })} />
              <Field label="Country" value={hotel.hotel_country} editable onSave={v => patch({ hotel_country: v })} />
              <Field label="Address" value={hotel.hotel_address} editable onSave={v => patch({ hotel_address: v })} />
              <Field label="Hotel Phone" value={hotel.hotel_phone} editable type="tel" onSave={v => patch({ hotel_phone: v })} />
              <Field label="Hotel Email" value={hotel.hotel_email} editable type="email" onSave={v => patch({ hotel_email: v })} />
              <Field label="Room Type" value={hotel.room_type} editable onSave={v => patch({ room_type: v })} />
              <Field label="Number of Rooms" value={hotel.num_rooms ? String(hotel.num_rooms) : "1"} editable type="number" onSave={v => patch({ num_rooms: parseInt(v) || 1 })} />
              <div>
                <Field label="Meal Plan" value={hotel.meal_plan} editable
                  options={MEAL_PLAN_OPTIONS}
                  onSave={v => patch({ meal_plan: v })} />
              </div>
              <Toggle
                label="Breakfast Included"
                value={!!hotel.breakfast_included}
                onSave={v => patch({ breakfast_included: v })}
              />
              <Field label="Supplier" value={hotel.supplier_name} editable onSave={v => patch({ supplier_name: v })} />
            </div>
          </div>

          {/* Stay details */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Stay Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Check-in" value={hotel.check_in} editable type="date" onSave={v => patch({ check_in: v })} />
              <Field label="Check-out" value={hotel.check_out} editable type="date" onSave={v => patch({ check_out: v })} />
              <Field label="Nights" value={String(nightCount)} />
              <Field label="Selling Price / Night (€)" value={hotel.selling_price_per_night ? String(hotel.selling_price_per_night) : ""} editable type="number"
                onSave={v => patch({ selling_price_per_night: parseFloat(v) || null })} />
              <Field label="Total Selling Price (€)" value={hotel.selling_price ? String(hotel.selling_price) : ""} editable type="number"
                onSave={v => patch({ selling_price: parseFloat(v) || 0 })} />
              <Field label="Currency" value={hotel.currency || "EUR"} editable
                options={["EUR","USD","GBP","AED","SAR"].map(c => ({ value: c, label: c }))}
                onSave={v => patch({ currency: v })} />
            </div>
          </div>

          {/* Cancellation policy */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Cancellation Policy</h2>
            <div className="space-y-4">
              <Field label="Policy Type" value={cancelPolicyType} editable
                options={CANCEL_POLICY_OPTIONS}
                onSave={v => patch({ cancellation_policy_type: v })} />
              {cancelPolicyType === "free_until_date" && (
                <Field label="Free Until Date" value={hotel.cancellation_deadline_date} editable type="date"
                  onSave={v => patch({ cancellation_deadline_date: v })} />
              )}
              {(cancelPolicyType === "partially_refundable" || cancelPolicyType === "custom") && (
                <Field label="Cancellation Conditions" value={hotel.cancellation_conditions} editable
                  onSave={v => patch({ cancellation_conditions: v })} />
              )}
              <Field label="Policy (legacy/display text)" value={hotel.cancellation_policy} editable
                onSave={v => patch({ cancellation_policy: v })} />
            </div>
          </div>

          {/* Internal info (booking channel) */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-sm font-bold text-amber-800 uppercase tracking-wider">Internal Information</h2>
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-semibold">Never shown to customer</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Booking Channel" value={hotel.booking_channel} editable onSave={v => patch({ booking_channel: v })} />
              <Field label="Booking Channel Reference" value={hotel.booking_channel_reference} editable onSave={v => patch({ booking_channel_reference: v })} />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Notes</h2>
            <div className="space-y-4">
              <Field label="Notes (Customer)" value={hotel.notes} editable onSave={v => patch({ notes: v })} />
              <Field label="Internal Notes" value={hotel.internal_notes} editable onSave={v => patch({ internal_notes: v })} />
            </div>
          </div>
        </div>
      )}

      {/* Accounting tab */}
      {tab === "accounting" && (
        <HotelAccountingPanel hotelId={id} hotel={hotel} invoiceNumber={hotel.invoice_number} />
      )}

      {/* Invoice tab */}
      {tab === "invoice" && (
        <HotelInvoiceSection hotelId={id} hotel={hotel} onUpdate={load} />
      )}

      {/* Voucher tab */}
      {tab === "voucher" && (
        <HotelVoucherPanel hotel={hotel} onUpdate={load} />
      )}
    </div>
    </DashboardLayout>
  );
}
